import { useMemo } from 'react'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { complement, isNil, find, propEq } from 'ramda'
import styled from 'styled-components'
import { breakpoints } from '@/styles'
import Skeleton from '@/components/skeleton'
import toArray from '@/utils/to-array'
import useBasic from '@/hooks/use-basic'
import { formatPhone } from '@/utils/format-phone'
import { DisplayTable } from '@/features/Application/elements/tables'
import {
  LabeledValue,
  LabelText,
  ValueText,
  Wrapper,
  EmptyComponent
} from '@/features/Application/elements'

const StyledLabledValue = styled(LabeledValue)`
  grid-template-columns: calc(25% - 12px) 1fr;
  @media only screen and (max-width: ${breakpoints.desktop}) {
    grid-template-columns: 1fr 1fr;
  }
`

const StoryStyledLabledValue = styled(StyledLabledValue)`
  @media only screen and (max-width: ${breakpoints.tabletLandscape}) {
    grid-template-columns: 1fr;
    > :first-child {
      display: none;
    }
  }
`

const notNull = complement(isNil)

const RenterInformation = ({ application = {}, applicationLoading }) => {
  const { t } = useTranslation()
  const { countries } = useBasic(['languages', 'countries'])
  const getCountry = countryCode => {
    const userCountry = find(propEq('txt_id', countryCode.toLowerCase()), toArray(countries))
    return userCountry?.name
  }
  const renderAddress = (userData = {}) => {
    const unit = userData.unit_number ? `${userData.unit_number} - ` : ''
    const address = userData.address ? `${userData.address}, ` : ''
    const city = userData.city ? `${userData.city}, ` : ''
    const state = userData.state ? `${userData.state} ` : ''
    const zip = userData.zip?.toUpperCase() || ''
    const country = userData.country_code ? ` ${getCountry(userData.country_code)}` : ''
    return `${unit}${address}${city}${state}${zip}${country}`
  }
  const title = t('l.application.view.renter_information.label', 'Renter Information')

  const data = useMemo(
    () => [
      {
        id: 'legal name',
        cell: (
          <StyledLabledValue
            key="legal-name"
            label={<LabelText>{t('l.application.view.legal_name.label', 'Legal Name')}</LabelText>}
            value={
              applicationLoading ? (
                <Skeleton width={'100%'} height={20} />
              ) : (
                <ValueText>{`${application?.user_info?.first_name} ${application?.user_info?.last_name}`}</ValueText>
              )
            }
          />
        )
      },
      {
        id: 'Date of Birth',
        cell: (
          <StyledLabledValue
            key="Date of Birth"
            label={<LabelText>{t('l.application.view.dob.label', 'Date of Birth')}</LabelText>}
            value={
              applicationLoading ? (
                <Skeleton width={'100%'} height={20} />
              ) : (
                <ValueText>{moment(application?.user_info?.dob).format('MM-DD-YYYY')}</ValueText>
              )
            }
          />
        )
      },
      {
        id: 'Phone Number',
        cell: (
          <StyledLabledValue
            key="Phone Number"
            label={<LabelText>{t('l.application.view.phone_num.label', 'Phone Number')}</LabelText>}
            value={
              applicationLoading ? (
                <Skeleton width={'100%'} height={20} />
              ) : (
                <ValueText>{`${formatPhone(
                  application?.user_info?.phone_number,
                  application?.user_info?.phone_country_code_alpha
                )}`}</ValueText>
              )
            }
          />
        )
      },
      {
        id: 'Email',
        cell: (
          <StyledLabledValue
            key="Email"
            label={<LabelText>{t('l.application.view.email.label', 'Email')}</LabelText>}
            value={
              applicationLoading ? (
                <Skeleton width={'100%'} height={20} />
              ) : (
                <ValueText>{`${application?.user_info?.username}`}</ValueText>
              )
            }
          />
        )
      },
      {
        id: 'Desired Move-in date',
        cell: (
          <StyledLabledValue
            key="Desired Move-in date"
            label={
              <LabelText>
                {t('l.application.view.desired_move_in_date.label', 'Desired Move-in date')}
              </LabelText>
            }
            value={
              applicationLoading ? (
                <Skeleton width={'100%'} height={20} />
              ) : (
                <ValueText>
                  {application.move_in_date
                    ? moment(application.move_in_date).format('MM-DD-YYYY')
                    : t('r.application.create.as_soon_as_possible.label', 'As soon as possible')}
                </ValueText>
              )
            }
          />
        )
      },
      {
        id: 'Current Address',
        cell: (
          <StyledLabledValue
            key="Current Address"
            label={
              <LabelText>
                {t('l.application.view.current_address.label', 'Current Address')}
              </LabelText>
            }
            value={
              applicationLoading ? (
                <Skeleton width={'100%'} height={20} />
              ) : (
                <ValueText>
                  {notNull(application?.user_info?.company_info?.address)
                    ? renderAddress(application?.user_info?.company_info)
                    : '-'}
                </ValueText>
              )
            }
          />
        )
      },
      {
        id: 'Story',
        cell: (
          <StoryStyledLabledValue
            key="Story"
            label={<LabelText>{t('l.application.view.story.label', 'Story')}</LabelText>}
            value={
              applicationLoading ? (
                <Skeleton width={'100%'} height={20} />
              ) : (
                <ValueText>
                  {notNull(application?.user_info?.about_me) ? application.user_info.about_me : '-'}
                </ValueText>
              )
            }
          />
        )
      }
    ],
    [applicationLoading]
  )

  return (
    <Wrapper>
      <DisplayTable
        data={data}
        title={title}
        columns={1}
        emptyComponent={
          <EmptyComponent isLoading={applicationLoading}>
            {t(
              'l.application.view.no_employment.label',
              'No employment references included in this application.'
            )}
          </EmptyComponent>
        }
      />
    </Wrapper>
  )
}

export default RenterInformation
